import React from 'react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { TTriState } from '../../../models/TTriState.js';
import { ComponentBase, InjectedStyledComponent, KEYS } from '../../../shared/index.js';
import Translate from '../Translate/Translate.js';

import { ICheckboxConfiguration, ICheckboxProps, ICheckboxState } from './models/index.js';
import Icon from './styledComponents/Icon.js';
import Label from './styledComponents/Label.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CHECKBOX_CATEGORY_KEY, CHECKBOX_COMPONENT_KEY } from './Checkbox.meta.js';

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

export class Checkbox extends ComponentBase<ICheckboxProps, ICheckboxState, ICheckboxConfiguration> {
	categoryKey = CHECKBOX_CATEGORY_KEY;
	componentKey = CHECKBOX_COMPONENT_KEY;

	@observable checked: TTriState = false;
	inputReference: HTMLElement | undefined;

	constructor(props: ICheckboxProps) {
		super(props);

		const { value } = this.props;
		if (value) {
			this.checked = value;
		}
		this.handleKeyDown = this.handleKeyDown.bind(this);

		makeObservable(this);
	}

	@computed
	get defaultChecked() {
		return this.checked && this.checked === true;
	}

	@computed
	get asset() {
		const icon = this.checked === 'half' ? 'half' : this.checked ? 'checked' : 'unchecked';
		return `base.checkbox.${icon}`;
	}

	@computed
	get accessibilityText() {
		const icon = this.checked === 'half' ? 'half' : this.checked ? 'checked' : 'unchecked';
		return icon;
	}

	onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { disabled, onChange } = this.props;
		if (!disabled) {
			this.setChecked(!this.checked);
			if (onChange) {
				onChange(event);
			}
		}
	};

	assignReference(reference: HTMLElement | null) {
		if (reference) {
			this.inputReference = reference;
		}
	}

	@action
	setChecked(checked: TTriState) {
		runInAction(() => {
			this.checked = checked;
		});
	}

	componentDidUpdate() {
		const { value } = this.props;
		if (value !== undefined && this.checked !== value) {
			this.setChecked(value);
		}
	}

	handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
		const { key } = e;
		if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1) {
			if (this.inputReference) {
				this.inputReference.click();
			}
		}
	};

	render() {
		const { className, disabled, label, labelId } = this.props;

		return (
			<Wrapper className={className}>
				<Label tabIndex={0} id={labelId} onKeyDown={(event: any) => this.handleKeyDown(event)}>
					<input
						type="checkbox"
						aria-checked="mixed"
						checked={this.defaultChecked}
						onChange={this.onChange}
						disabled={disabled}
						style={{ display: 'none' }}
						ref={(reference) => this.assignReference(reference)}
					/>
					<Icon asset={this.asset} aria-label={this.accessibilityText} />
					<Translate>{label}</Translate>
				</Label>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Checkbox), CHECKBOX_CATEGORY_KEY, CHECKBOX_COMPONENT_KEY);
